/**
 * storageService — avatar upload to Firebase Storage
 *
 * Path: avatars/{uid}
 * After upload, updates Firestore users/{uid}.photoURL
 */
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { storage, db } from '../config/firebase';

const MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_TYPES  = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export function validateAvatarFile(file: File): string | null {
  if (!ALLOWED_TYPES.includes(file.type)) return 'Only JPG, PNG, WebP, or GIF images are allowed.';
  if (file.size > MAX_SIZE_BYTES) return 'Image must be smaller than 2 MB.';
  return null;
}

/**
 * Upload avatar image to Firebase Storage and update Firestore photoURL.
 * Returns the download URL.
 */
export async function uploadAvatar(uid: string, file: File): Promise<string> {
  const error = validateAvatarFile(file);
  if (error) throw new Error(error);

  const storageRef = ref(storage, `avatars/${uid}`);
  await uploadBytes(storageRef, file, { contentType: file.type });
  const downloadURL = await getDownloadURL(storageRef);

  // Update Firestore
  await updateDoc(doc(db, 'users', uid), {
    photoURL:  downloadURL,
    updatedAt: serverTimestamp(),
  });

  return downloadURL;
}

/**
 * Delete avatar from Storage and clear photoURL in Firestore.
 */
export async function deleteAvatar(uid: string): Promise<void> {
  try {
    const storageRef = ref(storage, `avatars/${uid}`);
    await deleteObject(storageRef);
  } catch {
    // Ignore if file doesn't exist
  }
  await updateDoc(doc(db, 'users', uid), {
    photoURL:  null,
    updatedAt: serverTimestamp(),
  });
}
